import DashboardTableShare from "@/components/DashboardTableShare/DashboardTableShare";
import FileDownloader from "@/components/FileDownloader/FileDownloader";
import Table, { Column } from "@/components/Table/Table";
import useTableState from "@/components/Table/hooks/useTableState";
import useTranslation from "@/hooks/useTranslation";
import { useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import { formatAmountWithUnit, formatTransportLabel } from "@/pages/dashboard/utils/formatters";
import { DownloadOutlined } from "@ant-design/icons";
import { useEffect } from "react";
import Widget from "../../components/Widget/Widget";
import {
  useTransportImpactDetail,
  useTransportImpactDetailExport,
} from "../../hooks/useTransportImpact";
import { ImpactMetric } from "../../utils/types";

export default function TransportImpactPerTransport({ metric }: { metric: ImpactMetric }) {
  const translation = useTranslation();
  const { pagination, sort, search, filter, onSearchFilterSortPaginate, setPagination } =
    useTableState({
      defaultPagination: { page: 1, pageSize: 20 },
      defaultSort: {
        key: metric.key,
        order: "descend",
      },
    });
  const { startDate, endDate } = useDateRange();

  useEffect(() => {
    setPagination({ ...pagination, page: 1 });
  }, [startDate, endDate]);

  const columns: Column[] = [
    {
      label: "Departure",
      key: "departure_name",
      maxWidth: "2fr",
      searchable: true,
      sortable: true,
    },
    {
      label: "Arrival",
      key: "arrival_name",
      maxWidth: "2fr",
      searchable: true,
      sortable: true,
    },
    {
      label: "Transported material",
      key: "total_product_mass",
      sortable: true,
      renderer: (record) => {
        return `${formatAmountWithUnit(record.total_product_mass, "kg", 0)}`;
      },
    },
    {
      label: "Total distance",
      key: "distance",
      sortable: true,
      renderer: (record) => {
        return `${formatAmountWithUnit(record.distance, "km", 0)}`;
      },
    },
    {
      label: "Transport modes",
      key: "transport_modes",
      filterable: true,
      renderer: (record) => {
        return record.transport_modes.map(formatTransportLabel).join(", ");
      },
    },
    {
      label: "Share",
      key: "share",
      maxWidth: "1fr",
      renderer: (record) => {
        return (
          <DashboardTableShare
            total={record[metric.key]}
            value={data?.company_transport_impact[metric.key]}
          />
        );
      },
    },
    {
      label: metric.label,
      key: metric.key,
      sortable: true,
      renderer: (record) => metric.formatter(record[metric.key]),
    },
  ];

  const { data, isLoading, isFetching } = useTransportImpactDetail({
    pagination,
    filter,
    search,
    sort,
    metric: metric.key,
  });

  const exportMutation = useTransportImpactDetailExport({
    pagination,
    filter,
    search,
    sort,
    metric: metric.key,
  });

  return (
    <Widget
      title={"Transport impact side-by-side"}
      tooltip={translation["dashboard"]["transport"]["side_by_side"]}
      topRight={
        <>
          <FileDownloader
            fileUrlFn={() => exportMutation.mutateAsync()}
            buttonText={<DownloadOutlined />}
            fileOutputName="transport-impact-by-route.xlsx"
            addTimestampAndCompany={true}
          />
        </>
      }
    >
      <Table
        availableFilters={{
          transport_modes: [
            { label: "Truck", value: "Truck" },
            { label: "Sea", value: "Sea" },
            { label: "Air", value: "Air" },
            { label: "Commercial vehicle", value: "Commercial vehicle" },
          ],
        }}
        hideRaster={true}
        type={"grey"}
        filter={filter}
        sort={sort}
        search={search}
        columns={columns}
        data={data?.transport_impact}
        loading={isLoading || isFetching}
        onSearchFilterSortPaginate={onSearchFilterSortPaginate}
        pagination={pagination}
        totalRows={data?.total_rows}
        rowKey={"id"}
      />
    </Widget>
  );
}
